import type { ApplicationContext } from '@hc-frontend/core-context';
import {
  CountMetricName,
  JourneyAlias,
  Placement,
  ProductType,
} from '@hc-frontend/core-types';
import type { IOperationError } from '@hc-frontend/core-utils-business-operation';
import {
  operationConcat,
  unsafe_validateEntity,
} from '@hc-frontend/core-utils-business-operation';

import type { PartialSurveyAnswers } from '../../entities';
import { MoveToNextPlateOptions, PlateName } from '../../entities';
import type { Plate } from '../../entities/journey';
import { Journey } from '../../entities/journey';
import type { JourneyRepository, MoveToPlateResult } from '../../repositories';
import type { GetAnswersUseCase } from '../get-answers/get-answers';
import type { UpdateLeadProductUseCase } from '../update-lead-product';

export type MoveToNextPlateUseCase = (
  options: MoveToNextPlateOptions,
) => Promise<MoveToPlateResult>;

export type MoveToNextPlateFactoryParams = Pick<
  ApplicationContext,
  'log' | 'trackCountMetric'
> &
  Pick<JourneyRepository, 'getJourney' | 'moveToPlate' | 'getNextPlate'> & {
    updateLeadProduct: (
      currentProductType: ProductType,
      newProductType: ProductType,
    ) => ReturnType<UpdateLeadProductUseCase>;
    getAnswers: GetAnswersUseCase;
  };

async function logOperation<T extends string>(
  log: ApplicationContext['log'],
  result: IOperationError<T>,
) {
  log({
    context: 'server',
    message: result.code,
    type: 'error',
    data: {
      rootCause: result.rootCause,
    },
  });
}

function handleMoveToNextPlateFactory({
  getJourney,
  log,
  getNextPlate,
  getAnswers,
}: Omit<MoveToNextPlateFactoryParams, 'updateLeadProduct'>) {
  return async ({
    journeyAlias,
    userId,
    sessionId,
    domainUserId,
    domainSessionId,
    currentPlate,
    surveyAnswers,
  }: Omit<MoveToNextPlateOptions, 'productType'>) => {
    const getJourneyOperation = operationConcat(getJourney)
      .then(unsafe_validateEntity(Journey, 'failedToGetJourney'))
      .onErrorTap((error) => logOperation(log, error))
      .done();

    const getJourneyResult = await getJourneyOperation(journeyAlias);

    if (!getJourneyResult.success) {
      logOperation(log, getJourneyResult.errors[0]);

      return getJourneyResult;
    }

    const execute = operationConcat(getAnswers)
      .onErrorDefaultTo<PartialSurveyAnswers>(() => Promise.resolve({}))
      .then(async (answers) => {
        return getNextPlate({
          currentPlate,
          journey: getJourneyResult.data,
          answers: {
            ...answers,
            ...surveyAnswers,
          },
        });
      })
      .onErrorTap((error) => logOperation(log, error))
      .done();

    const result = await execute(
      sessionId,
      userId,
      domainSessionId,
      domainUserId,
    );

    if (result.success) {
      return {
        ...result,
        data: { plate: result.data, journey: getJourneyResult.data },
      };
    }

    return result;
  };
}

function buildCountMetric(params: {
  journey: Journey;
  plateIndex: string;
  productType: ProductType;
  plateName: string;
}) {
  return {
    name: CountMetricName.Enum['page-view'],
    tags: {
      platform: 'apollo',
      journey_id: params.journey?.id.toString(),
      plate_name: params.plateName,
      plate_index: params.plateIndex,
      product: params.productType,
      placement: Placement.Enum.SURVEY,
      journey_end: true,
    },
  };
}

export function moveToNextPlateFactory({
  getJourney,
  moveToPlate,
  updateLeadProduct,
  getNextPlate,
  getAnswers,
  log,
  trackCountMetric,
}: MoveToNextPlateFactoryParams): MoveToNextPlateUseCase {
  const handleMoveToNextPlate = handleMoveToNextPlateFactory({
    getJourney,
    moveToPlate,
    getNextPlate,
    getAnswers,
    log,
    trackCountMetric,
  });

  const handleMedicareRedirect = (
    journeyAlias: JourneyAlias,
    options: MoveToNextPlateOptions,
  ) =>
    operationConcat(handleMoveToNextPlate)
      .then(({ plate }) =>
        moveToPlate({
          plate,
          journeyAlias,
        }),
      )
      .done()({
      ...options,
      journeyAlias,
      surveyAnswers: undefined,
      currentPlate: undefined,
    });

  const handleHealthRedirect = (productType: ProductType, plateToMove: Plate) =>
    operationConcat(updateLeadProduct)
      .then(() =>
        moveToPlate({
          plate: plateToMove,
          journeyAlias: JourneyAlias.Enum.HEALTH_DEFAULT,
          targetsExternalApp: true,
        }),
      )
      .done()(productType, ProductType.Enum.HEALTH);

  return operationConcat(
    unsafe_validateEntity(MoveToNextPlateOptions, 'failedToMoveToPlate'),
  )
    .thenJoin(handleMoveToNextPlate)
    .then(([parsedOptions, { plate: plateToMove, journey }]) => {
      if (plateToMove.name === PlateName.Enum.REDIRECT_UNDER_65) {
        trackCountMetric(
          buildCountMetric({
            journey,
            plateIndex: plateToMove.order.toString(),
            productType: parsedOptions.productType,
            plateName: 'redirect_quotes_healthcare_com',
          }),
        );

        return handleHealthRedirect(parsedOptions.productType, plateToMove);
      }
      if (plateToMove.name === PlateName.Enum.REDIRECT_MEDICARE_ADVANTAGE) {
        trackCountMetric(
          buildCountMetric({
            journey,
            plateIndex: plateToMove.order.toString(),
            productType: parsedOptions.productType,
            plateName: 'redirect_med_advantage',
          }),
        );

        return handleMedicareRedirect(
          JourneyAlias.Enum.MEDICARE_ADVANTAGE,
          parsedOptions,
        );
      }
      if (plateToMove.name === PlateName.Enum.REDIRECT_MEDICARE_SUPPLEMENT) {
        trackCountMetric(
          buildCountMetric({
            journey,
            plateIndex: plateToMove.order.toString(),
            productType: parsedOptions.productType,
            plateName: 'redirect_medicare',
          }),
        );

        return handleMedicareRedirect(
          JourneyAlias.Enum.MEDICARE_SUPPLEMENT,
          parsedOptions,
        );
      }

      return moveToPlate({
        plate: plateToMove,
        journeyAlias: parsedOptions.journeyAlias,
      });
    })
    .onErrorTap(async (result) => logOperation(log, result))
    .done();
}
